import { useEffect, useMemo, useState } from "react";
import { logError, LogCategory } from "utils/sentryLogger";
import { fetchCourses, fetchCurricula, fetchImItems, fetchUnits } from "./imQuestionBrowserApi";

export const ALL_SECTION_TYPE_OPTION = { value: "__all__", label: "All" };

const IM_PUBLISHER = "Illustrative Mathematics";

const resolveImCurriculum = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const byPublisher = rows.find((row) => row.publisher === IM_PUBLISHER);
  if (byPublisher) return byPublisher;
  const byName = rows.find((row) => row.name?.includes(IM_PUBLISHER));
  if (byName) return byName;
  return rows[0];
};

const toGradeOption = (course) => ({
  value: course.id,
  label: course.name || `Grade ${course.grade_level}`,
  raw: course,
});

const toUnitOption = (unit) => ({
  value: unit.id,
  label: unit.ordinal_name ? `${unit.ordinal_name}: ${unit.name}` : unit.name,
  raw: unit,
});

const toLessonOption = (lesson) => ({
  value: lesson.id,
  label: lesson.ordinal_name ? `${lesson.ordinal_name}: ${lesson.name}` : lesson.name,
  raw: lesson,
});

/**
 * Grade -> Unit -> Lesson -> Section-type cascade for the IM Question Browser.
 *
 * Multiple curricula exist (Eureka Math, Eureka Math², Illustrative Mathematics,
 * OKO Differentiation), but this browser is IM-only, so the IM curriculum is
 * auto-resolved from GET curriculum (matched by publisher/name, see
 * resolveImCurriculum) and never surfaced as a dropdown. Units come back with
 * their lessons already embedded, so selecting a unit derives the lesson
 * options client-side — no per-unit lessons fetch.
 * Selecting a lesson fetches the IM items; section type is a pure client-side
 * filter over that response (no refetch).
 */
export const useImQuestionBrowserFilters = () => {
  const [curriculumId, setCurriculumId] = useState(null);

  const [grades, setGrades] = useState([]);
  const [grade, setGrade] = useState(null);
  const [gradesLoading, setGradesLoading] = useState(false);

  const [units, setUnits] = useState([]);
  const [unit, setUnit] = useState(null);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const [lessons, setLessons] = useState([]);
  const [lesson, setLesson] = useState(null);

  const [sectionType, setSectionType] = useState(ALL_SECTION_TYPE_OPTION);

  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState(null);

  const [error, setError] = useState(null);

  // Auto-resolve the Illustrative Mathematics curriculum — no dropdown for it.
  useEffect(() => {
    let cancelled = false;
    fetchCurricula()
      .then((rows) => {
        if (cancelled) return;
        const im = resolveImCurriculum(rows);
        setCurriculumId(im?.id ?? null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        logError("IM Question Browser: failed to load curriculum", e, {
          category: LogCategory.ERROR,
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Grade (course) options for the resolved curriculum.
  useEffect(() => {
    setGrades([]);
    setGrade(null);
    if (!curriculumId) return undefined;
    let cancelled = false;
    setGradesLoading(true);
    fetchCourses(curriculumId)
      .then((rows) => {
        if (cancelled) return;
        setGrades((rows || []).map(toGradeOption));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        logError("IM Question Browser: failed to load grades", e, {
          category: LogCategory.ERROR,
        });
      })
      .finally(() => {
        if (!cancelled) setGradesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [curriculumId]);

  // Selecting a grade loads its units (with lessons embedded) and resets
  // unit/lesson/section-type.
  useEffect(() => {
    setUnits([]);
    setUnit(null);
    setLessons([]);
    setLesson(null);
    setSectionType(ALL_SECTION_TYPE_OPTION);
    setItems([]);
    if (!grade) return undefined;
    let cancelled = false;
    setUnitsLoading(true);
    fetchUnits(grade.value)
      .then((rows) => {
        if (cancelled) return;
        setUnits((rows || []).map(toUnitOption));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        logError("IM Question Browser: failed to load units", e, {
          category: LogCategory.ERROR,
        });
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [grade]);

  // Selecting a unit derives lesson options from its embedded `lessons` (no
  // fetch) and resets lesson/section-type.
  useEffect(() => {
    setLesson(null);
    setSectionType(ALL_SECTION_TYPE_OPTION);
    setItems([]);
    if (!unit) {
      setLessons([]);
      return;
    }
    setLessons((unit.raw?.lessons || []).map(toLessonOption));
  }, [unit]);

  // Selecting a lesson fetches its IM items and resets section-type to "All".
  useEffect(() => {
    setSectionType(ALL_SECTION_TYPE_OPTION);
    setItems([]);
    setItemsError(null);
    if (!lesson) return undefined;
    let cancelled = false;
    setItemsLoading(true);
    fetchImItems({ lessonId: lesson.value })
      .then((data) => {
        if (cancelled) return;
        setItems(data?.items || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setItemsError(e);
        logError("IM Question Browser: failed to load items", e, {
          category: LogCategory.ERROR,
        });
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lesson]);

  // Section-type options are the distinct curriculum_label values present in
  // the currently-loaded items, plus a leading "All".
  const sectionTypeOptions = useMemo(() => {
    const labels = [...new Set(items.map((item) => item.curriculum_label).filter(Boolean))];
    return [ALL_SECTION_TYPE_OPTION, ...labels.map((label) => ({ value: label, label }))];
  }, [items]);

  const filteredItems = useMemo(() => {
    if (!sectionType || sectionType.value === ALL_SECTION_TYPE_OPTION.value) return items;
    return items.filter((item) => item.curriculum_label === sectionType.value);
  }, [items, sectionType]);

  return {
    grades,
    grade,
    setGrade,
    gradesLoading,
    units,
    unit,
    setUnit,
    unitsLoading,
    lessons,
    lesson,
    setLesson,
    sectionType,
    setSectionType,
    sectionTypeOptions,
    items,
    filteredItems,
    itemsLoading,
    itemsError,
    error,
  };
};
